import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Plus, Layers } from "lucide-react";

const BUTTON_CLASS =
    "inline-flex h-9 min-w-[11rem] items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.025] px-3 py-2 text-sm transition-colors";

export function PoolSwitcher({ pools, activeId, onSelect, onCreate }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, []);

    const list = pools || [];
    const active = list.find((p) => p.id === activeId) || list[0];

    return (
        <div className="relative h-9 shrink-0" ref={ref}>
            {active ? (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className={`${BUTTON_CLASS} hover:bg-white/[0.05]`}
                    data-testid="pool-switcher-button"
                >
                    <Layers className="h-3.5 w-3.5 text-white/55" />
                    <span className="font-medium text-white">{active.name || "Unnamed pool"}</span>
                    <span className="text-[10px] text-white/40 font-mono">{list.length} pool{list.length === 1 ? "" : "s"}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
            ) : (
                <div className={`${BUTTON_CLASS} invisible`} aria-hidden data-testid="pool-switcher-placeholder">
                    <Layers className="h-3.5 w-3.5" />
                    <span className="font-medium">Pool</span>
                </div>
            )}
            {active && open && (
                <div className="absolute left-0 mt-2 w-72 z-50 rounded-xl border border-white/[0.08] bg-[#1A1A1A] p-1.5 shadow-2xl" data-testid="pool-switcher-menu">
                    <div className="px-2.5 pt-2 pb-1.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-white/40">Pools</div>
                    {list.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => { onSelect(p.id); setOpen(false); }}
                            className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.05] flex items-center justify-between"
                            data-testid={`pool-switch-${p.id}`}
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">{p.name || "Unnamed pool"}</p>
                                <p className="text-[11px] text-white/40 capitalize">{p.cadence || "—"} · {(p.members || []).length} recipients</p>
                            </div>
                            {p.id === activeId && <Check className="h-4 w-4 text-[#00BF0D]" />}
                        </button>
                    ))}
                    <div className="border-t border-white/[0.06] mt-1.5 pt-1.5">
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onCreate?.(); }}
                            className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.05] flex items-center gap-2.5 text-sm text-[#00BF0D] font-medium"
                            data-testid="pool-create-button"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Create new pool</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
